import React, { Fragment, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useCapacitorStorage from 'Util/Hooks/useCapacitorStorage';
import useDisclosure from 'Util/Hooks/useDisclosure';
import { myProfile } from 'Redux/user-profile/selectors';
import { Box, Flex, SvgIcon as BaseSvgIcon } from 'Components/Base';
import { useSearchMyCourseProvider } from 'Context/SearchMyCourseProvider';
import { useConfig } from 'Context/ConfigContext';
import { useMyCoursesTracking } from 'Util/Analytics';

import SearchSuggestion from './SearchSuggestion';

const Input = styled.input`
  ${({ theme }) => css`
    box-sizing: border-box;
    background: transparent;
    flex: 1;
    min-width: 100px;
    height: 28px;
    padding-left: 8px;
    font-size: ${theme.fontSizes[1]}px;
    font-family: ${theme.fonts.sans};
    border: none;
    :focus-visible {
      outline: none;
    }
    ::placeholder {
      opacity: 30%;
      color: ${theme.colors.text};
    }
  `}
`;

const SearchInputWrapper = styled((props) => (
  <Flex px={4} py="6px" bg="white" alignItems="center" {...props} />
))`
  border-radius: 40px;
  overflow: hidden;
`;

const SvgIcon = styled(BaseSvgIcon)`
  ${({ theme }) => css`
    font-size: 24px;
    color: ${theme.colors.notiGrey.grey1};
  `}
`;

const SearchMyCourse = ({ user, ...props }) => {
  const { searchTerm, debouncedSearchTerm, handleSearchTermChange, resetSearchTerm } =
    useSearchMyCourseProvider();
  const searchInputRef = useRef();
  const { logCourseSearch } = useMyCoursesTracking();
  const { myCourses } = useConfig();
  const enableSearchSuggestion = !!myCourses?.enableSearchSuggestion;
  const [searchHistoryLists, setSearchHistoryLists] = useCapacitorStorage(
    [],
    `${user?.id}_search_history`
  );
  const [isOpenSearchSuggestion, handleOpenSearchSuggestion, handleCloseSearchSuggestion] =
    useDisclosure();

  const saveSearchHistoryLists = (list) => {
    if (list) {
      if (searchHistoryLists.includes(list)) {
        const indexOfDuplicateList = searchHistoryLists.indexOf(list);
        searchHistoryLists.splice(indexOfDuplicateList, 1);
      }
      if (searchHistoryLists.length > 3) {
        searchHistoryLists.pop();
      }
      setSearchHistoryLists([list, ...searchHistoryLists]);
    }
  };

  const hasSearchText = searchTerm !== '';

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      searchInputRef.current.blur();
    }
  };

  const handleOnBlur = () => {
    handleCloseSearchSuggestion();
    if (debouncedSearchTerm !== '') {
      saveSearchHistoryLists(searchTerm);
      logCourseSearch(debouncedSearchTerm);
    }
  };

  return (
    <Fragment>
      <Box onFocus={handleOpenSearchSuggestion} onBlur={handleOnBlur}>
        <SearchInputWrapper>
          <SvgIcon icon={SvgIcon.ICON_LIST.SEARCH} opacity={hasSearchText ? '100%' : '30%'} />
          <Input
            type="text"
            inputmode="search"
            name="searchMyCourse"
            placeholder="ค้นหาคอร์ส"
            onChange={(event) => {
              handleSearchTermChange(event.target.value);
            }}
            value={searchTerm}
            onClick={handleOpenSearchSuggestion}
            onKeyDown={handleKeyDown}
            ref={searchInputRef}
          />
          {hasSearchText && (
            <SvgIcon
              opacity="30%"
              icon={SvgIcon.ICON_LIST.CLOSE_CIRCLE}
              onClick={() => {
                resetSearchTerm();
                handleCloseSearchSuggestion();
              }}
            />
          )}
        </SearchInputWrapper>
        {enableSearchSuggestion && isOpenSearchSuggestion && (
          <SearchSuggestion
            onHandleCloseSearchSuggestion={handleCloseSearchSuggestion}
            searchHistoryLists={searchHistoryLists}
            saveSearchHistoryLists={saveSearchHistoryLists}
          />
        )}
      </Box>
    </Fragment>
  );
};

SearchMyCourse.propTypes = {
  user: PropTypes.object,
};

export default connect((state) => ({ user: myProfile(state) }), {})(SearchMyCourse);
